import { Injectable } from '@angular/core';
import { AbstractControl, FormArray } from '@angular/forms';
import { Customer, Script, ServiceType } from '@generated/tcea-call-center/models';
import { OrderStatusResponse } from '@generated/tcea-call-center/models/order-status-response';
import { TcaServiceType } from '@generated/tcea-call-center/models/tca-service-type';
import { CustomerUser } from '@generated/tcea/models/customer-user';
import { StoreDetails } from '@interfaces/consumer-api/store/store-details';
import { Store } from '@interfaces/store/1.0';
import { CustomerAddress } from '@generated/tcea-call-center/models/customer-address';
import { Cart } from '@models/call-center/cart';
import { BehaviorSubject } from 'rxjs';
import { DeliveryAddress } from '@generated/tcea-call-center/models/delivery-address';
import { AddressType } from '@generated/tcea-call-center/models/address-type';
import {
  ApiKeyDetailResponse,
  ServiceType as DispositionServiceType,
} from '@generated/tcea/models';
import { StoreMenu } from '@generated/tcea/models/store-menu';

export enum OrderStep {
  ServiceType = 'service-type',
  Menu = 'menu',
  Checkout = 'checkout',
  Disposition = 'disposition',
}

@Injectable({
  providedIn: 'root',
})
export class CallCenterOrderSetupService {
  private currentStore: Store = null;
  private currentStoreDetails: StoreDetails = null;
  private xApiKey: ApiKeyDetailResponse = null;
  private serviceType: TcaServiceType = TcaServiceType.Pickup;
  private orderStep: OrderStep = OrderStep.ServiceType;
  private cart: Cart = null;
  private currentCustomer: Customer | CustomerUser = null;
  private dispositionDate: Date = null;
  private interactionId: string = '';
  private isSubmitting: boolean = false;
  private orderCreated: OrderStatusResponse = null;
  private customerAddress: CustomerAddress = null;
  private callCenterId: string = '';
  private storeMenu: StoreMenu = null;
  private scripts: Script[] = [];
  private creditCardTransaction: any = null;
  private isMenuLoading: boolean = true;
  private customerFavorites: { [key: string]: any } = {};
  private deliveryAddress: DeliveryAddress = null;
  private ANI: string = '';
  private DNIS: string = '';

  private serviceTypeSubject = new BehaviorSubject<TcaServiceType>(this.serviceType);
  private orderStepSubject = new BehaviorSubject<OrderStep>(this.orderStep);
  private currentStoreSubject = new BehaviorSubject<Store>(this.currentStore);
  private xApiKeySubject = new BehaviorSubject<ApiKeyDetailResponse>(this.xApiKey);
  private currentCustomerSubject = new BehaviorSubject<Customer | CustomerUser>(
    this.currentCustomer,
  );
  private interactionIdSubject = new BehaviorSubject<string>(this.interactionId);
  private cartSubject = new BehaviorSubject<Cart>(this.cart);
  private dispositionDateSubject = new BehaviorSubject<Date>(this.dispositionDate);
  private currentStoreDetailsSubject = new BehaviorSubject<StoreDetails>(this.currentStoreDetails);
  private isSubmittingSubject = new BehaviorSubject<boolean>(this.isSubmitting);
  private orderCreatedSubject = new BehaviorSubject<OrderStatusResponse>(this.orderCreated);
  private customerAddressSubject = new BehaviorSubject<CustomerAddress>(this.customerAddress);
  private callCenterIdSubject = new BehaviorSubject<string>(this.callCenterId);
  private storeMenuSubject = new BehaviorSubject<StoreMenu>(this.storeMenu);
  private scriptsSubject = new BehaviorSubject<Script[]>(this.scripts);
  private isMenuLoadingSubject = new BehaviorSubject<boolean>(this.isMenuLoading);
  private customerFavoritesSubject = new BehaviorSubject<{ [key: string]: any }>(
    this.customerFavorites,
  );
  private deliveryAddressSubject = new BehaviorSubject<DeliveryAddress>(this.deliveryAddress);

  serviceType$ = this.serviceTypeSubject.asObservable();
  orderStep$ = this.orderStepSubject.asObservable();
  currentStore$ = this.currentStoreSubject.asObservable();
  xApiKey$ = this.xApiKeySubject.asObservable();
  currentCustomer$ = this.currentCustomerSubject.asObservable();
  interactionId$ = this.interactionIdSubject.asObservable();
  cart$ = this.cartSubject.asObservable();
  dispositionDate$ = this.dispositionDateSubject.asObservable();
  currentStoreDetails$ = this.currentStoreDetailsSubject.asObservable();
  isSubmitting$ = this.isSubmittingSubject.asObservable();
  orderCreated$ = this.orderCreatedSubject.asObservable();
  customerAddress$ = this.customerAddressSubject.asObservable();
  callCenterId$ = this.callCenterIdSubject.asObservable();
  storeMenu$ = this.storeMenuSubject.asObservable();
  scripts$ = this.scriptsSubject.asObservable();
  isMenuLoading$ = this.isMenuLoadingSubject.asObservable();
  customerFavorites$ = this.customerFavoritesSubject.asObservable();
  deliveryAddress$ = this.deliveryAddressSubject.asObservable();

  getServiceType(): TcaServiceType {
    return this.serviceTypeSubject.value;
  }

  getOrderStep(): OrderStep {
    return this.orderStepSubject.value;
  }

  getCurrentStore(): Store {
    return this.currentStoreSubject.value;
  }

  getXApiKey(): ApiKeyDetailResponse {
    return this.xApiKeySubject.value;
  }

  getScripts(): Script[] {
    return this.scriptsSubject.value;
  }

  getCurrentCustomer(): Customer | CustomerUser {
    return this.currentCustomerSubject.value;
  }

  getInteractionId(): string {
    return this.interactionIdSubject.value;
  }

  getCart(): Cart {
    return this.cartSubject.value;
  }

  getDispositionDate(): Date {
    return this.dispositionDateSubject.value;
  }

  getCurrentStoreDetails(): StoreDetails {
    return this.currentStoreDetailsSubject.value;
  }

  getIsSubmitting(): boolean {
    return this.isSubmittingSubject.value;
  }

  getOrderCreated(): OrderStatusResponse {
    return this.orderCreatedSubject.value;
  }

  getCustomerAddress(): CustomerAddress {
    return this.customerAddressSubject.value;
  }

  setCustomerAddress(customerAddress: CustomerAddress) {
    this.customerAddressSubject.next(customerAddress);
  }

  getDeliveryAddress(): DeliveryAddress {
    return this.deliveryAddressSubject.value;
  }

  setDeliveryAddress(address: DeliveryAddress) {
    this.deliveryAddressSubject.next(address);
  }

  getCallCenterId(): string {
    return this.callCenterIdSubject.value;
  }

  getANI(): string {
    return this.ANI;
  }

  getDNIS(): string {
    return this.DNIS;
  }

  setCallDetails(ANI: string, DNIS: string) {
    this.ANI = ANI;
    this.DNIS = DNIS;
  }

  setOrderStep(step: OrderStep) {
    this.orderStepSubject.next(step);
  }

  setCurrentStore(store: Store) {
    this.currentStoreSubject.next(store);
  }

  setXApiKey(xApiKey: ApiKeyDetailResponse) {
    this.xApiKeySubject.next(xApiKey);
  }

  setScripts(scripts: Script[]) {
    this.scriptsSubject.next(scripts);
  }

  setCurrentCustomer(customer: Customer | CustomerUser) {
    this.currentCustomerSubject.next(customer);
  }

  setInteractionId(interactionId: string) {
    this.interactionIdSubject.next(interactionId);
  }

  setCart(cart: Cart) {
    this.cartSubject.next(cart);
  }

  setDispositionDate(dispositionDate: Date) {
    this.dispositionDateSubject.next(dispositionDate);
  }

  setCurrentStoreDetails(storeDetails: StoreDetails) {
    this.currentStoreDetailsSubject.next(storeDetails);
  }

  setIsSubmitting(isSubmitting: boolean) {
    this.isSubmittingSubject.next(isSubmitting);
  }

  setOrderCreated(orderCreated: OrderStatusResponse) {
    this.orderCreatedSubject.next(orderCreated);
  }

  setServiceType(serviceType: TcaServiceType) {
    this.serviceTypeSubject.next(serviceType);
  }

  setCallCenterId(callCenterId: string) {
    this.callCenterIdSubject.next(callCenterId);
  }

  removeCartItem(itemId: string) {
    const currentCart = this.getCart();
    if (!currentCart || !currentCart.items) {
      return;
    }

    const updatedCart = {
      ...currentCart,
      items: currentCart.items.filter((item) => item.id !== itemId),
    };

    this.setCart(updatedCart);
  }

  getStorePaymentMethods(): string[] {
    const storeDetails = this.getCurrentStoreDetails();
    if (storeDetails?.serviceTypesConfigurations) {
      return Object.values(storeDetails.serviceTypesConfigurations)
        .flatMap((config: any) => config.tenderConfigurations)
        .map((tenderConfig: any) => tenderConfig?.tenderType)
        .filter((tenderType: string) => tenderType !== 'DummyVal')
        .filter((value, index, self) => self.indexOf(value) === index);
    }
    return [];
  }

  reset() {
    this.setCart(null);
    this.setOrderStep(OrderStep.ServiceType);
    this.setCurrentStore(null);
    this.setCurrentStoreDetails(null);
    this.setCurrentCustomer(null);
    this.setInteractionId(null);
    this.setDispositionDate(null);
    this.setIsSubmitting(false);
    this.setOrderCreated(null);
    this.setCallCenterId('');
    this.setStoreMenu(null);
    this.setCustomerFavorites({});
    this.setCreditCardTransaction(null);
    this.setDeliveryAddress(null);
    this.setCallDetails('', '');
  }

  getCustomerId(): string | undefined {
    const customer = this.currentCustomerSubject.value;
    if (!customer) return undefined;

    return 'id' in customer ? customer.id : (customer as CustomerUser).userId;
  }

  convertCustomerAddressToDeliveryAddress(): DeliveryAddress | null {
    const customerAddress = this.getCustomerAddress();
    if (!customerAddress) return null;

    const customer = this.getCurrentCustomer();

    return {
      address1: customerAddress.address1,
      address2: customerAddress.address2 || undefined,
      city: customerAddress.city,
      state: customerAddress.state,
      country: customerAddress.country,
      postalCode: customerAddress.postalcode,
      coordinates: {
        latitude: customerAddress.coordinates?.latitude,
        longitude: customerAddress.coordinates?.longitude,
      },
      type: this.mapAddressType(customerAddress.type as 'Home' | 'Work' | 'School' | 'Other'),
      label: customerAddress.label,
      phone: this.isCustomer(customer) ? customer.phoneNumber : '',
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      email: customer?.email,
      businessName: undefined,
      deliveryInstructions: undefined,
      saveAddress: false,
    };
  }

  private mapAddressType(type: 'Home' | 'Work' | 'School' | 'Other'): AddressType {
    switch (type) {
      case 'Home':
        return AddressType.Home;
      case 'Work':
        return AddressType.Work;
      case 'School':
        return AddressType.School;
      case 'Other':
        return AddressType.Other;
    }
  }

  private isCustomer(customer: Customer | CustomerUser): customer is Customer {
    return 'phoneNumber' in customer;
  }

  convertToServiceType(tcaServiceType: TcaServiceType): ServiceType {
    switch (tcaServiceType) {
      case TcaServiceType.Delivery:
        return ServiceType.DeliveryByChannel;
      default:
        return tcaServiceType as unknown as ServiceType;
    }
  }

  convertToCallCenterServiceType(serviceType: ServiceType): TcaServiceType {
    switch (serviceType) {
      case ServiceType.DeliveryByChannel:
        return TcaServiceType.Delivery;
      default:
        return serviceType as unknown as TcaServiceType;
    }
  }

  convertToDispositionServiceType(tcaServiceType: TcaServiceType): DispositionServiceType {
    switch (tcaServiceType) {
      case TcaServiceType.Delivery:
        return DispositionServiceType.DELIVERY_BY_CHANNEL;
      case TcaServiceType.Pickup:
        return DispositionServiceType.PICKUP;
      case TcaServiceType.DriveThru:
        return DispositionServiceType.DRIVE_THRU;
      case TcaServiceType.DineIn:
        return DispositionServiceType.DINE_IN;
      case TcaServiceType.BringToTable:
        return DispositionServiceType.BRING_TO_TABLE;
      case TcaServiceType.Curbside:
        return DispositionServiceType.CURBSIDE;
      default:
        return DispositionServiceType.PICKUP; // Default fallback
    }
  }

  getStoreMenu(): StoreMenu {
    return this.storeMenuSubject.value;
  }

  setStoreMenu(menu: StoreMenu) {
    this.storeMenuSubject.next(menu);
  }

  /**
   * Recursively serialize Item trees to ItemRequest arrays for cart/order payloads
   */
  serializeItemsForCart(items: any[] | undefined): any[] | undefined {
    if (!items || items.length === 0) return undefined;

    const mapItemRecursively = (item: any): any => {
      const mapped: any = {
        menuItemId: item.menuItemId,
        name: item.name,
        quantity: item.quantity,
        type: item.type,
      };

      if (item.instructionId) mapped.instructionId = item.instructionId;
      if (item.modifierGroupId) mapped.modifierGroupId = item.modifierGroupId;
      if (item.discounts) mapped.discounts = item.discounts;
      if (item.price !== undefined) mapped.price = item.price;
      if (item.totalPrice !== undefined) mapped.totalPrice = item.totalPrice;

      if (item.items && item.items.length > 0) {
        mapped.items = item.items.map((sub: any) => mapItemRecursively(sub));
      }

      return mapped;
    };

    return items.map((it) => mapItemRecursively(it));
  }

  /**
   * Utility: checks if a modifierId is selected in a selectedModifiers value (string or array).
   * Compares baseId before '#'.
   */
  isModifierIdSelected(
    modifierId: string,
    selected: string | string[] | null | undefined,
  ): boolean {
    if (!modifierId || !selected) return false;
    const baseTarget = modifierId.split('#')[0];
    if (Array.isArray(selected)) {
      return selected.some((id) => id && id.split('#')[0] === baseTarget);
    }
    if (typeof selected === 'string') {
      return selected.split('#')[0] === baseTarget;
    }
    return false;
  }

  getCurrentMenuId(): string {
    return this.storeMenuSubject.value?.menuId;
  }

  navigateToInitialSetup() {
    this.setOrderStep(OrderStep.ServiceType);
    this.setOrderCreated(null);
    this.setCart(null);
  }

  setCreditCardTransaction(transaction: any) {
    this.creditCardTransaction = transaction;
  }

  getCreditCardTransaction() {
    return this.creditCardTransaction;
  }

  clearCreditCardTransaction() {
    this.creditCardTransaction = null;
  }

  /**
   * Gets a product by catalogEntryId from the current store menu
   * @param catalogEntryId The catalog entry ID to look up
   * @returns The product if found, null otherwise
   */
  getProductByCatalogEntryId(catalogEntryId: string): any | null {
    const menu = this.getStoreMenu();
    if (!menu || !menu.products) return null;

    return menu.products[catalogEntryId] || null;
  }

  /**
   * Gets the consumer-api price for a product
   * @param product The product to get price for
   * @returns The price or 0 if not found
   */
  getProductConsumerApiPrice(product: any): number {
    if (!product) return 0;

    const currentDisposition = this.getServiceType();
    const serviceTypeDisposition = this.convertToDispositionServiceType(currentDisposition);

    // Check disposition pricing first
    if (product.dispositionPricing) {
      for (const channel of Object.values(product.dispositionPricing)) {
        let matchingPrice = undefined;
        if (Array.isArray(channel)) {
          matchingPrice = channel.find((price) => price.serviceType === serviceTypeDisposition);
        }
        if (matchingPrice) {
          return matchingPrice.baseValue || 0;
        }
      }
    }

    // Fall back to channel pricing
    return product.channelPricing?.['consumer-api'] || 0;
  }

  public getDisplayPricingPrice(
    item: { displayPricing?: { price?: string } },
    menu?: StoreMenu,
    brandId?: string,
  ): number | undefined {
    const displayPricingItemId = item?.displayPricing?.price;
    if (!displayPricingItemId) return undefined;

    return this.resolveDisplayPricingItemPrice(displayPricingItemId, menu, brandId);
  }

  public resolveDisplayPricingItemPrice(
    itemId: string,
    menu?: StoreMenu,
    brandId?: string,
  ): number | undefined {
    menu = menu || this.getStoreMenu();
    if (!menu) return undefined;

    const product = menu.products?.[itemId];
    if (product) {
      return this.getItemPrice(product, menu);
    }

    const composite = menu.composites?.[itemId];
    if (composite) {
      return this.getItemPrice(composite, menu);
    }

    const modifier = menu.modifiers?.[itemId];
    if (modifier) {
      return this.getModifierDisplayPrice(modifier, brandId);
    }

    return undefined;
  }

  private getModifierDisplayPrice(modifier: any, brandId?: string): number {
    const dispositionPrice = this.getDispositionDisplayPrice(modifier.dispositionPricing);
    if (dispositionPrice !== undefined) {
      return dispositionPrice;
    }

    brandId = brandId || window.sessionStorage.getItem('brand_id');
    return (
      modifier.channelPricing?.[brandId]?.['consumer-api'] ??
      modifier.channelPricing?.['consumer-api'] ??
      0
    );
  }

  private getDispositionDisplayPrice(dispositionPricing: any): number | undefined {
    if (!dispositionPricing) return undefined;

    const serviceTypeDisposition = this.convertToDispositionServiceType(this.getServiceType());

    for (const pricing of Object.values(dispositionPricing)) {
      const consumerApiPricing = Array.isArray(pricing)
        ? pricing
        : (pricing as any)?.['consumer-api'];

      if (!Array.isArray(consumerApiPricing)) continue;

      const matchingPrice = consumerApiPricing.find(
        (price) => price.serviceType === serviceTypeDisposition,
      );

      if (matchingPrice?.baseValue !== undefined) {
        return matchingPrice.baseValue;
      }
    }

    return undefined;
  }

  public getItemPrice(item: any, menu?: any): number {
    if (!item) return 0;
    menu = menu || this.getStoreMenu();

    // Handle composite items (combos/containers)
    if ('productGroupIds' in item) {
      let totalPrice = 0;
      if (item.channelPricing?.price) {
        totalPrice = item.channelPricing.price;
      }
      const currentDisposition = this.getServiceType();
      const serviceTypeDisposition = this.convertToDispositionServiceType(currentDisposition);
      item.productGroupIds.forEach((groupId: string) => {
        const group = menu.productGroups[groupId];
        if (!group) return;
        const groupOverride = item.productGroupOverrides?.[groupId];
        if (groupOverride) {
          groupOverride.defaultProductIds?.forEach((productId: string) => {
            const product = menu.products[productId];
            if (product) {
              let productPrice = product.channelPricing?.price || 0;
              if (product.dispositionPricing) {
                for (const channel of Object.values(product.dispositionPricing)) {
                  let matchingPrice = undefined;
                  if (Array.isArray(channel)) {
                    matchingPrice = channel.find(
                      (price) => price.serviceType === serviceTypeDisposition,
                    );
                  }
                  if (matchingPrice) {
                    productPrice = matchingPrice.baseValue || productPrice;
                    break;
                  }
                }
              }
              totalPrice += productPrice;
            }
          });
        } else {
          group.productIds?.forEach((productId: string) => {
            const product = menu.products[productId];
            if (product) {
              let productPrice = product.channelPricing?.price || 0;
              if (product.dispositionPricing) {
                for (const channel of Object.values(product.dispositionPricing)) {
                  let matchingPrice = undefined;
                  if (Array.isArray(channel)) {
                    matchingPrice = channel.find(
                      (price) => price.serviceType === serviceTypeDisposition,
                    );
                  }
                  if (matchingPrice) {
                    productPrice = matchingPrice.baseValue || productPrice;
                    break;
                  }
                }
              }
              totalPrice += productPrice;
            }
          });
        }
      });
      if (item.dispositionPricing) {
        for (const channel of Object.values(item.dispositionPricing)) {
          let matchingPrice = undefined;
          if (Array.isArray(channel)) {
            matchingPrice = channel.find((price) => price.serviceType === serviceTypeDisposition);
          }
          if (matchingPrice) {
            return matchingPrice.baseValue || totalPrice;
          }
        }
      }
      return totalPrice;
    }
    // Handle regular products
    const defaultPrice = item.channelPricing?.price || 0;
    if (item.dispositionPricing) {
      const currentDisposition = this.getServiceType();
      const serviceTypeDisposition = this.convertToDispositionServiceType(currentDisposition);
      for (const channel of Object.values(item.dispositionPricing)) {
        let matchingPrice = undefined;
        if (Array.isArray(channel)) {
          matchingPrice = channel.find((price) => price.serviceType === serviceTypeDisposition);
        }
        if (matchingPrice) {
          return matchingPrice.baseValue || defaultPrice;
        }
      }
    }
    return defaultPrice;
  }

  getIsMenuLoading(): boolean {
    return this.isMenuLoadingSubject.value;
  }

  setIsMenuLoading(isLoading: boolean) {
    this.isMenuLoadingSubject.next(isLoading);
  }

  getCustomerFavorites(): { [key: string]: any } {
    return this.customerFavoritesSubject.value;
  }

  setCustomerFavorites(favorites: { [key: string]: any }) {
    this.customerFavoritesSubject.next(favorites);
  }

  // Returns prettified dietary/allergy tag labels for a product/composite, including levelOfContainment for allergy tags
  public getProductDietaryAllergyTags(
    item: any,
    tagLookup: any,
  ): { type: string; label: string }[] {
    if (!item || !item.tagIds || !tagLookup) return [];
    const prettify = (name: string) =>
      name
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (m) => m.toUpperCase());
    const prettifyContainment = (containment: string) =>
      containment
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (m) => m.toUpperCase());

    const result = [];
    for (const tagId of item.tagIds) {
      const tag = tagLookup[tagId];
      if (!tag) continue;
      if (tag.type === 'DIETARY') {
        result.push({ type: 'DIETARY', label: prettify(tag.name) });
      } else if (tag.type === 'ALLERGY') {
        let label = prettify(tag.name);
        if (tag.levelOfContainment && tag.levelOfContainment[item.id]) {
          const containment = tag.levelOfContainment[item.id];
          label = `${prettifyContainment(containment)} ${prettify(tag.name)}`;
        }
        result.push({ type: 'ALLERGY', label });
      }
    }
    return result;
  }

  /**
   * Recursively validates a modifier group and its nested groups
   * @param group The modifier group form control
   * @returns True if valid, false otherwise
   */
  validateModifierGroupRecursive(group: AbstractControl): boolean {
    if (!group) return true;

    // Check the group's minimum quantity requirement
    const minQuantity = group.get('minimumQuantity')?.value || 0;
    if (minQuantity > 0) {
      const selectedModifiers = group.get('selectedModifiers')?.value || [];
      const selectedCount = Array.isArray(selectedModifiers)
        ? selectedModifiers.length
        : selectedModifiers
          ? 1
          : 0;
      if (selectedCount < minQuantity) {
        return false;
      }
    }

    // Get selected modifier IDs
    const selectedModifiers = group.get('selectedModifiers')?.value || [];
    const selectedModifierIds = Array.isArray(selectedModifiers)
      ? selectedModifiers
      : selectedModifiers
        ? [selectedModifiers]
        : [];

    // Validate nested groups for selected modifiers
    const modifiersArray = group.get('modifiers') as FormArray;
    if (!modifiersArray) return true;

    for (const modifierControl of modifiersArray.controls) {
      const modifierId = modifierControl.get('id')?.value;
      if (!modifierId) continue;

      const isSelected = selectedModifierIds.some((id: string) => {
        if (!id) return false;
        const baseId = id.split('#')[0];
        return baseId === modifierId;
      });

      if (isSelected) {
        // Check nested groups for this modifier
        const nestedGroups = modifierControl.get('nestedGroups') as FormArray;
        if (nestedGroups && nestedGroups.length > 0) {
          for (const nestedGroupControl of nestedGroups.controls) {
            if (!this.validateModifierGroupRecursive(nestedGroupControl)) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  /**
   * Checks if a modifier has nested groups with modifiers
   * @param modifierControl The modifier form control
   * @returns True if modifier has nested groups with modifiers
   */
  hasNestedGroups(modifierControl: AbstractControl): boolean {
    if (!modifierControl) return false;
    const nestedGroupsControl = modifierControl.get('nestedGroups');
    if (!nestedGroupsControl) return false;
    const nestedGroups = nestedGroupsControl as FormArray;
    if (!nestedGroups || nestedGroups.length === 0) return false;

    // Check if at least one nested group has modifiers
    return nestedGroups.controls.some((nestedGroup: AbstractControl) => {
      const modifiersControl = nestedGroup.get('modifiers') as FormArray;
      return modifiersControl && modifiersControl.length > 0;
    });
  }

  /**
   * Gets nested modifier groups for a modifier
   * @param modifierControl The modifier form control
   * @returns Array of nested group form controls or null if none exist
   */
  getNestedGroupControls(modifierControl: AbstractControl): FormArray | null {
    if (!modifierControl) return null;
    const nestedGroupsControl = modifierControl.get('nestedGroups');
    if (!nestedGroupsControl) return null;
    const nestedGroups = nestedGroupsControl as FormArray;
    if (!nestedGroups || !nestedGroups.controls || nestedGroups.length === 0) return null;
    return nestedGroups;
  }

  /**
   * Gets selected modifier IDs from a group, normalized to base IDs (without instruction)
   * @param group The modifier group form control
   * @returns Array of base modifier IDs
   */
  getSelectedModifierIds(group: AbstractControl): string[] {
    if (!group) return [];
    const selectedModifiers = group.get('selectedModifiers')?.value;
    if (!selectedModifiers) return [];

    const selectedArray = Array.isArray(selectedModifiers)
      ? selectedModifiers
      : [selectedModifiers];

    return selectedArray.filter((id: string) => id).map((id: string) => id.split('#')[0]);
  }

  /**
   * Recursively calculates the price for a modifier group and its nested groups
   * @param group The modifier group form control
   * @param itemQuantity The quantity of the parent item
   * @param modifierPriceGetter Callback function to get modifier price
   * @returns The total price for this group and its nested groups
   */
  calculateModifierGroupPrice(
    group: AbstractControl,
    itemQuantity: number,
    modifierPriceGetter: (modifierId: string) => number,
  ): number {
    if (!group) return 0;

    let groupPrice = 0;
    const selectedModifiers = group.get('selectedModifiers')?.value;

    // Handle both array (checkbox) and single (radio) selections
    if (Array.isArray(selectedModifiers)) {
      // Handle checkbox selections
      const modifierQuantities = group.get('modifierQuantities')?.value || {};
      selectedModifiers.forEach((modifierId: string) => {
        if (!modifierId) return;

        const [baseId] = modifierId.split('#');
        const modifierControl = (group.get('modifiers') as FormArray)?.controls.find(
          (mod: AbstractControl) => mod.get('id')?.value === baseId,
        );

        if (!modifierControl) return;

        // Use modifier quantity if modifierQuantities exists, otherwise default to 1
        const hasModifierQuantities = modifierQuantities[baseId];
        const modifierQuantity = modifierControl?.get('quantity')?.value || 0;
        const quantity = hasModifierQuantities ? modifierQuantity : 1;
        const price = modifierPriceGetter(modifierId);
        groupPrice += price * quantity * itemQuantity;

        // Calculate nested group prices
        const nestedGroups = modifierControl.get('nestedGroups') as FormArray;
        if (nestedGroups && nestedGroups.controls) {
          nestedGroups.controls.forEach((nestedGroup: AbstractControl) => {
            groupPrice += this.calculateModifierGroupPrice(
              nestedGroup,
              itemQuantity,
              modifierPriceGetter,
            );
          });
        }
      });
    } else if (selectedModifiers) {
      // Handle radio button selection
      const [baseId] = selectedModifiers.split('#');
      const modifierControl = (group.get('modifiers') as FormArray)?.controls.find(
        (mod: AbstractControl) => mod.get('id')?.value === baseId,
      );

      if (modifierControl) {
        const price = modifierPriceGetter(selectedModifiers);
        groupPrice += price * itemQuantity;

        // Calculate nested group prices
        const nestedGroups = modifierControl.get('nestedGroups') as FormArray;
        if (nestedGroups && nestedGroups.controls) {
          nestedGroups.controls.forEach((nestedGroup: AbstractControl) => {
            groupPrice += this.calculateModifierGroupPrice(
              nestedGroup,
              itemQuantity,
              modifierPriceGetter,
            );
          });
        }
      }
    }

    return groupPrice;
  }
}
